import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";

export default function ChangePassword({ open, onClose }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);

    try {
      await api.post("/auth/change-password", form);

      toast.success(
        t("changePassword.changedSuccess", {
          defaultValue:
            "Password changed — please log in again with your new password",
        })
      );

      logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(
        formatErr(
          err,
          t("changePassword.changeFailed", {
            defaultValue: "Could not change password",
          })
        )
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md rounded-2xl"
        data-testid="change-password-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">
            {t("changePassword.heading", {
              defaultValue: "Change password",
            })}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={submit}
          className="space-y-4 mt-2"
          data-testid="change-password-form"
        >
          <div>
            <Label htmlFor="cp-current">
              {t("changePassword.currentPassword", {
                defaultValue: "Current password",
              })}
            </Label>

            <Input
              id="cp-current"
              data-testid="cp-current"
              type="password"
              required
              autoComplete="current-password"
              className="mt-1.5"
              value={form.current_password}
              onChange={(e) =>
                setForm({
                  ...form,
                  current_password: e.target.value,
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="cp-new">
              {t("changePassword.newPassword", {
                defaultValue: "New password",
              })}
            </Label>

            <Input
              id="cp-new"
              data-testid="cp-new"
              type="password"
              required
              autoComplete="new-password"
              className="mt-1.5"
              value={form.new_password}
              onChange={(e) =>
                setForm({
                  ...form,
                  new_password: e.target.value,
                })
              }
            />

            <p className="text-[11px] text-slate-400 mt-1">
              {t("changePassword.requirements", {
                defaultValue:
                  "At least 8 characters with letters and numbers.",
              })}
            </p>
          </div>

          <div>
            <Label htmlFor="cp-confirm">
              {t("changePassword.confirmNewPassword", {
                defaultValue: "Confirm new password",
              })}
            </Label>

            <Input
              id="cp-confirm"
              data-testid="cp-confirm"
              type="password"
              required
              autoComplete="new-password"
              className="mt-1.5"
              value={form.confirm_password}
              onChange={(e) =>
                setForm({
                  ...form,
                  confirm_password: e.target.value,
                })
              }
            />
          </div>

          <Button
            type="submit"
            data-testid="cp-submit"
            disabled={busy}
            className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95"
          >
            {busy
              ? t("changePassword.updating", {
                  defaultValue: "Updating…",
                })
              : t("changePassword.updatePassword", {
                  defaultValue: "Update password",
                })}
          </Button>

          <p className="text-[11px] text-slate-400 text-center">
            {t("changePassword.logoutNotice", {
              defaultValue:
                "You'll be logged out of all devices after changing your password.",
            })}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}